// Short supervisory visit form — the AHFC one-page template, filled from the
// CRM (the same RPC serves the Caera app). Who is supervised is the first
// choice: a caregiver (PSS / CNA / family), an LPN, or an RN (AHFC policy, on
// demand). Tasks observed and concerns are picked from the client's care
// plan and the agency banks so a routine visit is a minute's work; only
// revisions / notes are typed. Signing completes the visit, files the report
// to the client's Drive folder on the agency template, and — for a scheduled
// visit with a cadence — books the next one.
//
// Opened by calendar visit (`visitId`) for scheduled visits, or by row
// (`supervisorVisitId`) for backfilled history that has no calendar visit.
import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { getSupervisionContext, submitSupervisoryVisit, fileSupervisionReport, KIND_LABEL, SUBJECT_KINDS, REASONS } from '../../lib/supervision';

const field = { width: '100%', padding: '7px 9px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };
const label = { fontSize: 10, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 };
const section = { fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--accent)', margin: '16px 0 8px', paddingBottom: 4, borderBottom: '1px solid var(--border)' };
const fmtD = (d) => d ? format(new Date(`${String(d).slice(0, 10)}T12:00:00`), 'MM/dd/yyyy') : '—';
const OTHER = '__other__';

function YesNo({ value, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {[[true, 'Yes'], [false, 'No']].map(([v, l]) => (
        <button key={l} type="button" disabled={disabled} onClick={() => onChange(v)}
          style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
            background: value === v ? (v ? 'var(--success)' : 'var(--danger)') : 'var(--bg2)', color: value === v ? 'var(--white)' : 'var(--text2)' }}>{l}</button>
      ))}
    </div>
  );
}

// A grouped multi-select: every option is a line on the report. `extra` is
// the one free-text line for anything the banks don't cover.
function PickList({ groups, selected, onToggle, extra, onExtra, disabled, placeholder }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg2)', padding: '6px 10px', maxHeight: 260, overflowY: 'auto' }}>
      {groups.map(g => (
        <div key={g.label} style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '6px 0 3px' }}>{g.label}</div>
          {g.items.map(t => (
            <label key={t} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, padding: '3px 0', cursor: disabled ? 'default' : 'pointer', color: selected.has(t) ? 'var(--text)' : 'var(--text2)' }}>
              <input type="checkbox" checked={selected.has(t)} disabled={disabled} onChange={() => onToggle(t)} style={{ marginTop: 3 }} />
              <span>{t}</span>
            </label>
          ))}
        </div>
      ))}
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, padding: '3px 0', borderTop: '0.5px solid var(--border)', marginTop: 4 }}>
        <input type="checkbox" checked={selected.has(OTHER)} disabled={disabled} onChange={() => onToggle(OTHER)} style={{ marginTop: 3 }} />
        <span style={{ flex: 1 }}>
          Other
          {selected.has(OTHER) && <input style={{ ...field, marginTop: 4 }} value={extra} disabled={disabled} placeholder={placeholder} onChange={e => onExtra(e.target.value)} />}
        </span>
      </label>
    </div>
  );
}

const STANDARD_NOTE = (nextDue) => `Plan of care reviewed with the caregiver. Current orders, ADL support, feeding, positioning and emergency protocols remain appropriate for the member's needs; no revisions were indicated at this visit.\nCaregiver demonstrated competency in all tasks observed.${nextDue ? ` Next supervisory visit due on or before ${nextDue}.` : ''}`;

export default function SupervisionShortForm({ visitId = null, supervisorVisitId = null, onDone, onCancel }) {
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [filed, setFiled] = useState(null); // { link } after signing
  const [f, setF] = useState({
    subject_kind: 'aide', supervised_staff_id: '', supervised_family_name: '', visit_date: format(new Date(), 'yyyy-MM-dd'),
    visit_start_time: '10:00', visit_duration_minutes: 60,
    conducted_in_home: true, reason: 'routine', care_per_plan: true, competent: true, environment_safe: true, condition_changed: false,
    tasks: new Set(), tasks_other: '', concerns: new Set(), concerns_other: '',
    care_plan_reviewed: true, revisions_made: false, revisions_communicated: null, revisions_notes: '',
    followup_required: false, corrective_action_required: false, signed_name: '',
  });
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const toggle = (k, v) => setF(x => { const n = new Set(x[k]); n.has(v) ? n.delete(v) : n.add(v); return { ...x, [k]: n }; });

  useEffect(() => {
    getSupervisionContext({ visitId, supervisorVisitId }).then(c => {
      setCtx(c);
      const sv = c.supervisor_visit || {};
      const noConcern = (c.concern_bank || []).find(b => b.category === 'None')?.text;
      const savedTasks = String(sv.tasks_observed || '').split('\n').filter(Boolean);
      const savedConcerns = String(sv.concerns || '').split('\n').filter(Boolean);
      setF(x => ({ ...x,
        subject_kind: sv.subject_kind && sv.subject_kind !== 'client' ? sv.subject_kind : 'aide',
        supervised_staff_id: sv.supervised_staff_id || (sv.subject_kind ? '' : (c.candidates?.[0]?.staff_id || '')),
        supervised_family_name: sv.supervised_family_name || (sv.supervised_staff_id ? '' : (c.client?.responsible || '')),
        // Backfilled history carries the slot date; the nurse corrects it to the real one.
        visit_date: sv.visit_date && (sv.backfill || sv.status === 'complete') ? String(sv.visit_date).slice(0, 10) : x.visit_date,
        visit_start_time: sv.visit_start_time ? String(sv.visit_start_time).slice(0, 5) : x.visit_start_time,
        visit_duration_minutes: sv.visit_duration_minutes || x.visit_duration_minutes,
        reason: sv.reason || x.reason,
        signed_name: sv.nurse_signed_name || c.me?.name || '',
        tasks: new Set(savedTasks), concerns: new Set(savedConcerns.length ? savedConcerns : (noConcern ? [noConcern] : [])),
        revisions_notes: sv.revisions_notes || '',
        ...(sv.status === 'complete' ? { care_per_plan: sv.care_per_plan, competent: sv.competent, environment_safe: sv.environment_safe, condition_changed: sv.condition_changed, care_plan_reviewed: sv.care_plan_reviewed, revisions_made: sv.revisions_made, revisions_communicated: sv.revisions_communicated, conducted_in_home: sv.conducted_in_home } : {}),
      }));
      if (sv.report_drive_url) setFiled({ link: sv.report_drive_url });
    }).catch(e => setError(e.message));
  }, [visitId, supervisorVisitId]);

  const sv = ctx?.supervisor_visit || {};
  const candidates = ctx?.candidates || [];
  const rule = ctx?.rules?.[f.subject_kind];
  const isFamily = f.subject_kind === 'aide' && !f.supervised_staff_id;
  const complete = sv.status === 'complete';
  const me = ctx?.me;
  const lpnOnNurse = me?.position === 'LPN' && f.subject_kind !== 'aide';

  const taskGroups = useMemo(() => {
    const plan = ctx?.plan_tasks || [];
    const bank = (ctx?.task_bank || []).filter(b => (b.applies_to || []).includes(f.subject_kind));
    const cats = [...new Set(bank.map(b => b.category))];
    return [
      ...(plan.length ? [{ label: "This client's care plan", items: plan }] : []),
      ...cats.map(c => ({ label: c, items: bank.filter(b => b.category === c).map(b => b.text) })),
    ];
  }, [ctx, f.subject_kind]);
  const concernGroups = useMemo(() => {
    const bank = ctx?.concern_bank || [];
    const cats = [...new Set(bank.map(b => b.category))];
    return cats.map(c => ({ label: c, items: bank.filter(b => b.category === c).map(b => b.text) }));
  }, [ctx]);

  const linesOf = (sel, other) => [...sel].filter(t => t !== OTHER).concat(sel.has(OTHER) && other.trim() ? [other.trim()] : []);
  const nextDue = useMemo(() => {
    if (!(rule?.active && rule.interval_days > 0 && f.visit_date)) return null;
    const d = new Date(`${f.visit_date}T12:00:00`); d.setDate(d.getDate() + rule.interval_days);
    return format(d, 'MM/dd/yyyy');
  }, [rule, f.visit_date]);

  const required = [f.care_per_plan, f.competent, f.environment_safe, f.condition_changed, f.care_plan_reviewed].every(v => v !== null)
    && (f.supervised_staff_id || (isFamily && f.supervised_family_name.trim()))
    && linesOf(f.tasks, f.tasks_other).length > 0 && linesOf(f.concerns, f.concerns_other).length > 0
    && f.signed_name.trim() && !lpnOnNurse;

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const payload = {
        subject_kind: f.subject_kind, supervised_staff_id: f.supervised_staff_id || null, supervised_family_name: isFamily ? f.supervised_family_name : null,
        visit_date: f.visit_date, visit_start_time: f.visit_start_time || null, visit_duration_minutes: Number(f.visit_duration_minutes) || null,
        conducted_in_home: f.conducted_in_home, reason: f.reason,
        care_per_plan: f.care_per_plan, competent: f.competent, environment_safe: f.environment_safe, condition_changed: f.condition_changed,
        tasks_observed: linesOf(f.tasks, f.tasks_other).join('\n'), concerns: linesOf(f.concerns, f.concerns_other).join('\n'),
        care_plan_reviewed: f.care_plan_reviewed, revisions_made: f.revisions_made, revisions_communicated: f.revisions_made ? f.revisions_communicated : null,
        revisions_notes: f.revisions_notes.trim() || STANDARD_NOTE(nextDue),
        followup_required: f.followup_required, corrective_action_required: f.corrective_action_required,
        signed_name: f.signed_name, source: 'crm', credential: me?.position || 'RN',
      };
      const r = await submitSupervisoryVisit({ visitId, supervisorVisitId }, payload);
      let link = null;
      try { link = await fileSupervisionReport(r.supervisor_visit_id); }
      catch (e) { setError(`Signed and completed, but the report could not be filed to Drive: ${e.message}. Use "File report" to retry.`); }
      setFiled({ link });
      onDone && onDone({ ...r, report: link });
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const refile = async () => {
    setBusy(true); setError('');
    try { setFiled({ link: await fileSupervisionReport(sv.id, { force: true }) }); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  if (!ctx && !error) return <div style={{ padding: 20, color: 'var(--text3)' }}>Loading…</div>;
  return (
    <div>
      {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--danger)', marginBottom: 10 }}>{error}</div>}
      {complete && (
        <div style={{ background: 'var(--success-bg)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--success)', marginBottom: 10, display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <span>Completed {fmtD(sv.visit_date)} by {sv.nurse_signed_name}{sv.supervising_credential ? `, ${sv.supervising_credential}` : ''}.</span>
          <span>{filed?.link ? <a href={filed.link} target="_blank" rel="noreferrer">Open filed report ↗</a> : <button className="btn btn-outline btn-sm" disabled={busy} onClick={refile}>{busy ? 'Filing…' : 'File report'}</button>}</span>
        </div>
      )}
      {!complete && sv.backfill && (
        <div style={{ background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--warn)', marginBottom: 10 }}>
          Backfilled history — this slot was generated from the start of service. Set the date the visit actually took place, then document it.
        </div>
      )}

      <div style={section}>Visit &amp; member information</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><div style={label}>Member</div><div style={{ fontSize: 13 }}>{ctx?.client?.name} <span style={{ color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 11 }}>{ctx?.client?.ref}</span></div></div>
        <div><div style={label}>Medicaid ID</div><div style={{ fontSize: 13, fontFamily: 'var(--mono)' }}>{ctx?.client?.medicaid_id || '—'}</div></div>
        <div><div style={label}>Date of visit</div><input type="date" style={field} value={f.visit_date} disabled={complete} onChange={e => set('visit_date', e.target.value)} /></div>
        <div><div style={label}>Previous supervisory visit</div><div style={{ fontSize: 13, padding: '7px 0' }}>{fmtD(sv.previous_visit_date)}</div></div>
        <div><div style={label}>Visit start time</div><input type="time" style={field} value={f.visit_start_time} disabled={complete} onChange={e => set('visit_start_time', e.target.value)} /></div>
        <div><div style={label}>Duration</div>
          <select style={field} value={f.visit_duration_minutes} disabled={complete} onChange={e => set('visit_duration_minutes', Number(e.target.value))}>
            {[30, 45, 60, 90, 120].map(m => <option key={m} value={m}>{m < 60 ? `${m} minutes` : `${m / 60} hour${m > 60 ? 's' : ''}`}</option>)}
          </select></div>
        <div><div style={label}>Conducted in member's home</div><YesNo value={f.conducted_in_home} disabled={complete} onChange={v => set('conducted_in_home', v)} /></div>
        <div><div style={label}>Reason for visit</div><select style={field} value={f.reason} disabled={complete} onChange={e => set('reason', e.target.value)}>{REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        <div><div style={label}>Medication list</div><div style={{ fontSize: 13, padding: '7px 0', color: ctx?.client?.meds_active ? 'var(--text)' : 'var(--warn)' }}>{ctx?.client?.meds_active ? `${ctx.client.meds_active} active · reconciled ${fmtD(ctx.client.meds_reconciled_at)}` : 'No medication list in CAERA'}</div></div>
      </div>

      <div style={section}>Staff supervised</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <div style={label}>Who is being supervised</div>
          <select style={field} value={f.subject_kind} disabled={complete} onChange={e => { set('subject_kind', e.target.value); set('supervised_staff_id', ''); set('tasks', new Set()); }}>
            {SUBJECT_KINDS.map(k => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
        </div>
        <div>
          <div style={label}>Name</div>
          <select style={field} value={f.supervised_staff_id} disabled={complete} onChange={e => set('supervised_staff_id', e.target.value)}>
            <option value="">{f.subject_kind === 'aide' ? 'Family caregiver (not on roster)' : 'Choose…'}</option>
            {candidates.filter(c => c.kind === f.subject_kind).map(c => <option key={c.staff_id} value={c.staff_id}>{c.name} · {c.position}</option>)}
          </select>
          {isFamily && <input style={{ ...field, marginTop: 4 }} placeholder="Family caregiver name" value={f.supervised_family_name} disabled={complete} onChange={e => set('supervised_family_name', e.target.value)} />}
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
        {rule?.active && rule.interval_days > 0
          ? `Supervision interval: every ${rule.interval_days} days${rule.min_gap_days ? `, not less than ${rule.min_gap_days} days apart` : ''}${nextDue ? ` · next due ${nextDue}` : ''}`
          : 'No fixed interval — conducted as required (AHFC policy, not a GAPP requirement).'}
      </div>
      {lpnOnNurse && <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>A nurse must be supervised by an RN — sign in as an RN to complete this visit.</div>}

      <div style={section}>Observation of care</div>
      <div style={{ display: 'grid', gap: 8 }}>
        {[['care_per_plan', 'Care delivered per plan of care & physician orders'], ['competent', `${f.subject_kind === 'aide' ? 'Caregiver' : f.subject_kind.toUpperCase()} competent in assigned tasks`], ['environment_safe', 'Home environment safe and appropriate'], ['condition_changed', "Change in member's condition or needs since last visit"]].map(([k, l]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '0.5px solid var(--border)' }}>
            <span style={{ fontSize: 13 }}>{l}</span><YesNo value={f[k]} disabled={complete} onChange={v => set(k, v)} />
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={label}>Care / tasks observed during visit <span style={{ color: 'var(--text3)', textTransform: 'none', letterSpacing: 0 }}>· pick 3–5 that match the time of day</span></div>
        <PickList groups={taskGroups} selected={f.tasks} onToggle={t => toggle('tasks', t)} extra={f.tasks_other} onExtra={v => set('tasks_other', v)} disabled={complete} placeholder="Task and what you observed, in past tense" />
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={label}>Concerns identified / follow-up required</div>
        <PickList groups={concernGroups} selected={f.concerns} onToggle={t => toggle('concerns', t)} extra={f.concerns_other} onExtra={v => set('concerns_other', v)} disabled={complete} placeholder="What was observed → what you did → whether it was corrected" />
      </div>
      <div style={{ display: 'flex', gap: 18, marginTop: 8 }}>
        <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={f.followup_required} disabled={complete} onChange={e => set('followup_required', e.target.checked)} /> Follow-up visit required</label>
        <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={f.corrective_action_required} disabled={complete} onChange={e => set('corrective_action_required', e.target.checked)} /> Corrective action required (14 days)</label>
      </div>

      <div style={section}>Care plan review</div>
      <div style={{ display: 'grid', gap: 8 }}>
        {[['care_plan_reviewed', 'Member care plan reviewed this visit'], ['revisions_made', 'Revisions made'], ...(f.revisions_made ? [['revisions_communicated', 'Revisions communicated to assigned staff']] : [])].map(([k, l]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '0.5px solid var(--border)' }}>
            <span style={{ fontSize: 13 }}>{l}</span><YesNo value={f[k]} disabled={complete} onChange={v => set(k, v)} />
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={{ ...label, display: 'flex', justifyContent: 'space-between' }}>
          <span>Revisions / notes</span>
          {!complete && <button type="button" onClick={() => set('revisions_notes', STANDARD_NOTE(nextDue))} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', textTransform: 'none', letterSpacing: 0 }}>Use standard note</button>}
        </div>
        <textarea style={{ ...field, minHeight: 64 }} value={f.revisions_notes} disabled={complete} onChange={e => set('revisions_notes', e.target.value)} placeholder="Left blank, the standard routine note is used." />
      </div>

      <div style={section}>Signature</div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
        <div><div style={label}>Supervising nurse (print)</div><input style={field} value={f.signed_name} disabled={complete} onChange={e => set('signed_name', e.target.value)} /></div>
        <div><div style={label}>Credential</div><div style={{ fontSize: 13, padding: '7px 0' }}>{sv.supervising_credential || (me?.position === 'LPN' ? 'LPN' : 'RN')}</div></div>
      </div>
      {me?.position === 'LPN' && f.subject_kind === 'aide' && <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 6 }}>Conducted by an LPN: the RN co-signature will be due within {rule?.cosign_by_rn_days || 10} business days (GAPP §901.15.3).</div>}
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>Conducted in the member's home; not billable to Medicaid. Filed to the member's Drive folder on signing and submitted with GAPP service requests.</div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button className="btn btn-outline" onClick={onCancel}>Close</button>
        {!complete && <button className="btn btn-primary" disabled={busy || !required} title={required ? '' : 'Answer every Yes/No, name who was supervised, pick at least one task and one concern line, and sign'} onClick={submit}>{busy ? 'Signing…' : 'Sign, complete and file'}</button>}
      </div>
    </div>
  );
}
